// 통계 대시보드 (IMP-7)
import { useCallback, useEffect, useState } from "react";
import { daysAgo, isoDate, statsApi, type StatsSummary } from "../api/stats.ts";
import { ApiError } from "@taplink/web-shared";
import { DailyClicksChart } from "../components/DailyClicksChart.tsx";

/** 기간 프리셋 — 달력 격자와 씨름하지 않게 자주 쓰는 범위를 먼저 둔다 */
const PRESETS = [
  { label: "7일", days: 7 },
  { label: "30일", days: 30 },
  { label: "90일", days: 90 },
] as const;

export function StatsPage() {
  const [days, setDays] = useState<number>(7);
  const [summary, setSummary] = useState<StatsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (rangeDays: number) => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await statsApi.summary(daysAgo(rangeDays - 1), isoDate(new Date()), 5));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "통계를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [load, days]);

  if (error) return <div className="alert alert-error" role="alert">{error}</div>;
  if (!summary) return <p role="status">불러오는 중…</p>;

  return (
    <>
      {/* 필터는 한 줄로 차트 위에 — 아래 모든 수치를 같은 기간으로 묶는다 */}
      <div className="filter-row" role="group" aria-label="조회 기간">
        {PRESETS.map((preset) => (
          <button
            key={preset.days}
            type="button"
            className={days === preset.days ? "chip chip--on" : "chip"}
            aria-pressed={days === preset.days}
            onClick={() => setDays(preset.days)}
          >
            최근 {preset.label}
          </button>
        ))}
        <span className="hint">
          {summary.range.from} ~ {summary.range.to}
        </span>
      </div>

      {summary.range.truncated && (
        <div className="alert alert-info" role="status">
          현재 요금제는 통계를 <strong>{summary.range.retentionDays}일</strong>까지 보관합니다.
          그 이전 기간은 조회되지 않습니다.
        </div>
      )}

      {/* 데이터 재요청 중에도 이전 렌더를 유지한다 — 스켈레톤·레이아웃 점프 없음 */}
      <div className={loading ? "reloading" : undefined}>
        <section className="panel">
          <h2>요약</h2>
          <div className="kpi-grid">
            <Kpi label="기간 클릭" value={summary.rangeClicks} hero />
            <Kpi label="누적 클릭(전체 기간)" value={summary.totalClicks} />
            <Kpi label="전체 링크" value={summary.totalLinks} />
            <Kpi label="활성 링크" value={summary.activeLinks} />
          </div>
        </section>

        <section className="panel">
          <DailyClicksChart points={summary.daily} title="일별 클릭 수" />
        </section>

        <section className="panel">
          <h2>클릭 상위 링크</h2>
          {summary.topLinks.length === 0 ? (
            <p className="hint">이 기간에 클릭된 링크가 없습니다.</p>
          ) : (
            <div className="table-scroll">
              <table className="link-table">
                <caption className="sr-only">기간 내 클릭 상위 링크</caption>
                <thead>
                  <tr>
                    <th scope="col">단축 링크</th>
                    <th scope="col">원본</th>
                    <th scope="col">상태</th>
                    <th scope="col">클릭</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.topLinks.map((link) => (
                    <tr key={link.shortKey} className={link.isActive ? undefined : "row-inactive"}>
                      <th scope="row"><code>{link.shortUrl}</code></th>
                      <td className="truncate" title={link.originalUrl}>{link.originalUrl}</td>
                      {/* 상태 표시는 링크 목록과 **같은 모양**이어야 한다 —
                          같은 뜻이 화면마다 다르게 보이면 다른 것으로 읽힌다 */}
                      <td>
                        <span className={link.isActive ? "badge" : "badge badge-warn"}>
                          {link.isActive ? "활성" : "비활성"}
                        </span>
                      </td>
                      <td className="num">{link.clicks.toLocaleString("ko-KR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

function Kpi({ label, value, hero = false }: { label: string; value: number; hero?: boolean }) {
  return (
    <div className="kpi">
      <span className="kpi-label">{label}</span>
      {/* 큰 숫자는 비례 숫자체 — tabular-nums는 표 열 정렬용이라 표제 숫자에선 헐거워 보인다 */}
      <strong className={hero ? "kpi-value kpi-value--hero" : "kpi-value"}>
        {value.toLocaleString("ko-KR")}
      </strong>
    </div>
  );
}
